import { NavLink, useNavigate, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import t from "@/lang/lang";
import {
  useOmniContractShowQuery,
  useOmniContractUpdateStateMutation,
} from "@/services/backend/omni/contract/api/contract-api";
import { Contract } from "@/services/backend/omni/contract/types/contract";
import { ContractPdf } from "@/services/backend/omni/contract/components/contract-pdf";
import { ContractDeleteDialog } from "@/services/backend/omni/contract/components/contract-delete-dialog";
import { useOmniOfferShowQuery } from "@/services/backend/omni/offer/api/offer-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Button } from "@/shared/components/ui/button";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentStateAlert } from "@/shared/bsm/core/document/components/ui/document-state-alert";
import { DocumentStateDropdown } from "@/shared/bsm/core/document/components/ui/document-state-dropdown";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { OmniBreadcrumbs } from "../../../../_components/omni-breadcrumbs";

export function ContractRoute() {
  const { offerId, contractId } = useParams();

  const {
    data: contract,
    isLoading,
    error,
  } = useOmniContractShowQuery({
    id: contractId!,
  });
  const { data: offer } = useOmniOfferShowQuery({ id: offerId! });

  const breadcrumbs = contract && (
    <OmniBreadcrumbs
      customerId={contract.customerId}
      processId={contract.processId!}
    >
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/omni/${contract.processId!}/offers`}>
            {t("Angebote")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={offerPath(contract.processId!, offerId!)}>
            <ReferenceNumber referenceNumber={offer?.referenceNumber} />
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <ReferenceNumber referenceNumber={contract.referenceNumber} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </OmniBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {contract && <ContractPage contract={contract} offerId={offerId!} />}
    </ProcessPageMainTemplate>
  );
}

function offerPath(processId: string, offerId: string) {
  return `/processes/omni/${processId}/offers/${offerId}`;
}

function ContractPage(props: { contract: Contract; offerId: string }) {
  // The contract is a document of its own: its provider keeps its errors
  // apart from those of the offer.
  return (
    <PersistenceContextProvider>
      <ErrValidationFailedContextProvider>
        <Page {...props} />
      </ErrValidationFailedContextProvider>
    </PersistenceContextProvider>
  );
}

// The field names of each tab: the keys of the readiness check and of
// the validation errors of the fields shown on the tab.
const links = [
  {
    label: t("Basisdaten"),
    to: "data",
    fieldName: [
      "referenceNumber",
      "contractState",
      "startDate",
      "endDate",
      "title",
      "subtitle",
      "text",
      "offerReference",
      "offerDate",
      "offerId",
      "companyAddress",
      "customerId",
      "customerAddress",
      "branches",
      "serviceObjects",
      "paymentTerms",
      "billingInformation",
    ],
  },
  {
    label: t("Preise"),
    to: "pricing",
    fieldName: ["pricing", "pricingBy"],
  },
  {
    label: t("Leistungen"),
    to: "scope-of-works",
    fieldName: ["scopeOfWork"],
  },
  {
    label: t("Dateien"),
    to: "files",
  },
];

function Page({ contract, offerId }: { contract: Contract; offerId: string }) {
  const navigate = useNavigate();
  const contractsPath = `${offerPath(contract.processId!, offerId)}/contracts`;

  return (
    <DocumentPage>
      <DocumentStateAlert document={contract} />
      <DocumentPageHeader>
        <Button asChild variant="outline" className="whitespace-normal">
          <NavLink to={contractsPath}>
            <ArrowLeft className="size-4" />
            <span>{t("Angebot")}</span>
          </NavLink>
        </Button>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              fieldName={link.fieldName}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <DocumentPageActions>
          {!isImmutable(contract) && (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <ContractDeleteDialog
                contractId={contract.id}
                onDeleted={() => navigate(contractsPath)}
              />
            </>
          )}
          <DocumentStateDropdown
            document={contract}
            mutation={useOmniContractUpdateStateMutation}
          />
          <ContractPdf contract={contract} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
